import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSetAddOnMutation } from "@/store/rest_api";
import { AddOnType } from "@/model/interface/AddOn";
import { apiErrorMessage } from "@/util/apiError";

interface SimpleAddOnDialogProps {
  addOnType: AddOnType;
  title: string;
  inlineName: string;
  description: string;
  priceLabel: string;
  isFree: boolean;
  active: boolean;
  canceling: boolean;
  turnOffNote: string;
  endDate: string | null;
  onClose: () => void;
}

export function SimpleAddOnDialog({
  addOnType,
  title,
  inlineName,
  description,
  priceLabel,
  isFree,
  active,
  canceling,
  turnOffNote,
  endDate,
  onClose,
}: SimpleAddOnDialogProps) {
  const [setAddOn, { isLoading: isSaving }] = useSetAddOnMutation();
  const [confirmingTurnOff, setConfirmingTurnOff] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const apply = async (quantity: number, message: string) => {
    try {
      await setAddOn({ addOnType, quantity }).unwrap();
      toast.success(message);
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, `Failed to update ${inlineName}`));
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="text-foreground">
        <DialogHeader>
          <DialogTitle>{active ? title : `Add ${inlineName}`}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <p className="text-sm font-semibold tabular-nums">{priceLabel}</p>

        {canceling && (
          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="font-semibold">{endDate ? `Ends ${endDate}` : "Ends at your billing date"}</p>
            <p className="text-muted-foreground">Keep it to carry on with no new charge.</p>
          </div>
        )}

        {!active && !isFree && (
          <p className="rounded-lg bg-muted p-3 text-sm">
            Starts today. The rest of this billing period is prorated onto your next invoice.
          </p>
        )}

        {confirmingTurnOff ? (
          <div className="space-y-3 rounded-lg border border-destructive/40 p-3">
            <p className="text-sm">{turnOffNote}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmingTurnOff(false)}>
                Keep it
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={isSaving}
                onClick={() => apply(0, `${title} turned off`)}
              >
                Turn off {inlineName}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            {active && !canceling ? (
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setConfirmingTurnOff(true)}
              >
                Turn off {inlineName}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                {active && !canceling ? "Done" : "Cancel"}
              </Button>
              {canceling && (
                <Button disabled={isSaving} onClick={() => apply(1, `${title} will keep renewing`)}>
                  Keep {inlineName}
                </Button>
              )}
              {!active && (
                <Button disabled={isSaving} onClick={() => apply(1, `${title} added`)}>
                  Add {inlineName}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
